import {
  Component,
  EventEmitter,
  Input,
  NgModule,
  OnInit,
  Output,
} from '@angular/core';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from '@environments/environment';
import { SalesOrder } from '@core/domain-classes/sales-order';
declare var Stripe: any;

enum WindowSize {
  XXS = 'xxs',
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
}

interface StripeFrameSize {
  size: WindowSize;
  windowWidth: number;
  height: number;
  onErrorHeight: number;
}

@Component({
  selector: 'app-stripeconnect-payment',
  templateUrl: './stripeconnect-payment.component.html',
  styleUrls: ['./stripeconnect-payment.component.scss'],
})
export class StripeconnectPaymentComponent implements OnInit {
  @Input() stripeSubmit: Subject<boolean>;
  @Input('salesOrder') salesOrder: SalesOrder;
  private stripeAppId: string = environment?.stripePublishableKey;
  @Output() sendStripe = new EventEmitter<any>();
  @Output() purchaseBtnProcessing = new EventEmitter<any>();
  card: any;
  stripe: any;
  window: Window & typeof globalThis;
  cardNumberElement: any;
  stripeErrorMsg: string =
    'We are not able to connect with Stripe payment gateway. Please try to reload the website and if this error persists contact support';

  private avokeObserver: boolean = false;
  private stripeFrameSize: StripeFrameSize[] = [
    {
      size: WindowSize.XXS,
      windowWidth: 380,
      height: 165,
      onErrorHeight: 296,
    },
    {
      size: WindowSize.XS,
      windowWidth: 675,
      height: 165,
      onErrorHeight: 207,
    },
    {
      size: WindowSize.SM,
      windowWidth: 767,
      height: 81,
      onErrorHeight: 118,
    },
    {
      size: WindowSize.MD,
      windowWidth: 1081,
      height: 165,
      onErrorHeight: 207,
    },
    {
      size: WindowSize.LG,
      windowWidth: 1607,
      height: 81,
      onErrorHeight: 118,
    },
    {
      size: WindowSize.XL,
      windowWidth: 1608,
      height: 81,
      onErrorHeight: 100,
    },
  ];

  constructor(private toasterService: ToastrService) {}

  async ngOnInit() {
    await this.loadStripeform();
    this.stripeSubmit.subscribe((value) => {
      if (value) this.submit();
    });
  }

  private async loadStripeform(): Promise<boolean> {
    try {
      // Ensure Stripe is available
      if (!Stripe) {
        const error = new Error(this.stripeErrorMsg);
        this.toasterService.error(error.message);
        throw error;
      }
      this.delay(2000);
      this.stripe = Stripe(this.stripeAppId);
      await this.renderNewPayment();

      return true;
    } catch (e) {
      this.toasterService.error(this.stripeErrorMsg);
      return false;
    }
  }

  private get stripeIframeElement(): HTMLIFrameElement {
    return document.querySelector('#payment-element iframe');
  }

  //* This function is used to render the new stripe payment form (Payment Element)
  private async renderNewPayment(): Promise<void> {
    const currency =
      this.salesOrder.customer.priceLevel.name === 'US' ? 'usd' : 'cad';
    const options = {
      mode: 'payment',
      currency: currency,
      amount: Math.round(
        Number(Number(this.salesOrder.totalAmount).toFixed(2)) * 100
      ),
      paymentMethodCreation: 'manual',
      appearance: {
        theme: 'flat',
        variables: {
          colorPrimary: '#0570de',
          colorBackground: '#ffffff',
          colorDanger: '#df1b41',
          iconColor: '#0e2f60',
          fontFamily: '"Mulish", sans-serif',
          colorText: '#212529',
          spacingUnit: '4px',
          borderRadius: '5px',
        },
        rules: {
          '.Label': {
            fontSize: '14px',
            fontWeight: '500',
            marginBottom: '6px',
          },
        },
      },
    };

    try {
      const elements = this.stripe.elements(options);
      this.cardNumberElement = elements.create('payment', {
        fields: {
          billingDetails: {
            address: 'never',
          },
        },
      });

      this.cardNumberElement.on('focus', (_event) => {
        if (!this.avokeObserver) {
          this.stripeContainerResize();
        }
      });

      this.cardNumberElement.mount('#payment-element');
      await this.delay(2000);
    } catch (error) {
      console.error('Error rendering new payment element:', error);
      this.toasterService.error(this.stripeErrorMsg);
    }
  }

  private get stripeContainerElement(): HTMLDivElement {
    return document.querySelector('#payment-element');
  }

  //stripe payment form resize on handle the dynamically style
  private stripeContainerResize(): void {
    if (this.stripeIframeElement && this.stripeContainerElement) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.target !== this.stripeIframeElement) return;

          const newHeight = entry.contentRect.height;
          const windowWidth = this.window.innerWidth;

          // Helper function to find frame size by window size
          const findFrameSize = (size: WindowSize) =>
            this.stripeFrameSize.find((x) => x.size === size);

          const xlSize = findFrameSize(WindowSize.XL);
          const lgSize = findFrameSize(WindowSize.LG);
          const mdSize = findFrameSize(WindowSize.MD);
          const smSize = findFrameSize(WindowSize.SM);
          const xsSize = findFrameSize(WindowSize.XS);
          const xxsSize = findFrameSize(WindowSize.XXS);

          if (windowWidth >= xlSize.windowWidth && newHeight >= xlSize.height) {
            this.setHeightOnStripeElement(120);
          } else if (
            (windowWidth > mdSize.windowWidth ||
              windowWidth <= smSize.windowWidth) &&
            windowWidth > xsSize.windowWidth &&
            windowWidth <= lgSize.windowWidth &&
            newHeight >= lgSize.height
          ) {
            this.setHeightOnStripeElement(135);
          } else if (
            windowWidth <= mdSize.windowWidth &&
            newHeight >= mdSize.height
          ) {
            if (
              windowWidth <= xxsSize.windowWidth &&
              newHeight >= xxsSize.height
            ) {
              this.setHeightOnStripeElement(250);
            } else {
              this.setHeightOnStripeElement(240);
            }
          } else if (
            windowWidth > 1081 ||
            (windowWidth < 767 && windowWidth > 675)
          ) {
            this.setHeightOnStripeElement(110);
          } else {
            this.setHeightOnStripeElement(190);
          }
        }
      });
      resizeObserver.observe(this.stripeIframeElement);
      this.avokeObserver = true;
    }
  }

  private setHeightOnStripeElement(height: number): void {
    this.stripeContainerElement.style.height = `${height}px`;
    this.stripeContainerElement.style.transition = 'height 0.2s ease';
  }

  //* Utility function to create a delay using async/await
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private submit(): void {
    var scope = this;
    this.purchaseBtnProcessing.emit(true);
    const paymentMethodParams = {
      billing_details: {
        name: this.salesOrder.customer.contactEmail,
        address: {
          city: null,
          country: null,
          line1: null,
          line2: null,
          postal_code: null,
          state: null,
        },
      },
      metadata: {
        orderId: this.salesOrder.id,
        orderNumber: this.salesOrder.orderNumber,
        poNumber: this.salesOrder.poNumber,
        customerName: this.salesOrder.customer.customerName,
      },
    };
    this.stripe
      .createPaymentMethod('card', this.cardNumberElement, paymentMethodParams)
      .then(function (result) {
        if (result.error) {
          scope.purchaseBtnProcessing.emit(false);
          scope.toasterService.error(result.error.message);
        } else {
          const paymentObject = {
            id: result.paymentMethod.id,
            last4: result.paymentMethod.card.last4,
          };
          scope.sendStripe.emit(paymentObject);
        }
      });
  }
}

@NgModule({
  declarations: [StripeconnectPaymentComponent],
  exports: [StripeconnectPaymentComponent],
})
export class StripeconnectPaymentModule {}
